/**
 * Replacements for the `react-icons/*` glyphs the app used, mapped onto
 * lucide (and our brand SVGs). Same prop surface as react-icons for the props
 * we actually pass: `size`, `color`, `className`, `style`, `onClick`.
 */
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Bell,
  BookCheck,
  Briefcase,
  Building2,
  CalendarDays,
  ChartLine,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  CircleAlert,
  CirclePlus,
  CircleX,
  ClipboardCheck,
  EllipsisVertical,
  Eye,
  File,
  FileDown,
  FileSearch,
  FileText,
  Folder,
  Gavel,
  Globe,
  GripVertical,
  HardDrive,
  Headset,
  HeartHandshake,
  IdCard,
  Info,
  Keyboard,
  LayoutTemplate,
  Link,
  ListChecks,
  ListPlus,
  type LucideIcon,
  type LucideProps,
  Monitor,
  Network,
  Newspaper,
  PiggyBank,
  Save,
  Settings,
  Signature,
  SlidersHorizontal,
  Sparkles,
  Trash2,
  UserCheck,
  Users,
  X,
  Zap
} from "lucide-react";
import React from "react";

import { GoogleIcon, MicrosoftIcon, OktaIcon, OneDriveIcon, StripeIcon, WordIcon } from "./brands";

export type IconType = React.ComponentType<LucideProps>;
export type IconBaseProps = LucideProps;

/** Passport: not in lucide; simple booklet glyph. */
export const PassportIcon = React.forwardRef<SVGSVGElement, LucideProps>(function PassportIcon(
  { size = 24, color = "currentColor", strokeWidth = 2, ...rest },
  ref
) {
  return (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <circle cx="12" cy="10" r="3.5" />
      <path d="M8 17h8" />
    </svg>
  );
});

const alias = (I: LucideIcon | typeof PassportIcon) => I;

// react-icons/ai
export const AiOutlineAudit = alias(ClipboardCheck);
// react-icons/bi
export const BiTrash = alias(Trash2);
// react-icons/bs
export const BsPassport = alias(PassportIcon);
export const BsThreeDotsVertical = alias(EllipsisVertical);
// react-icons/cg
export const CgTemplate = alias(LayoutTemplate);
// react-icons/fa
export const FaCheck = alias(Check);
export const FaChevronLeft = alias(ChevronLeft);
export const FaChevronRight = alias(ChevronRight);
export const FaEllipsisV = alias(EllipsisVertical);
export const FaFolder = alias(Folder);
export const FaHandHoldingHeart = alias(HeartHandshake);
export const FaSave = alias(Save);
export const FaStripeS = StripeIcon;
export const FaUsers = alias(Users);
// react-icons/fi
export const FiAlertCircle = alias(CircleAlert);
export const FiUsers = alias(Users);
export const FiX = alias(X);
// react-icons/gi
export const GiConfirmed = alias(BadgeCheck);
// react-icons/hi
export const HiOutlineGlobe = alias(Globe);
// react-icons/hi2
export const HiOutlineSparkles = alias(Sparkles);
// react-icons/im
export const ImOnedrive = OneDriveIcon;
// react-icons/io5
export const IoArrowBack = alias(ArrowLeft);
export const IoArrowBackSharp = alias(ArrowLeft);
export const IoArrowForward = alias(ArrowRight);
export const IoArrowForwardSharp = alias(ArrowRight);
export const IoNotificationsOutline = alias(Bell);
// react-icons/lia
export const LiaPassportSolid = alias(PassportIcon);
// react-icons/lu
export const LuBriefcase = alias(Briefcase);
export const LuFileDown = alias(FileDown);
export const LuFileSearch = alias(FileSearch);
export const LuFileText = alias(FileText);
export const LuLineChart = alias(ChartLine);
export const LuNetwork = alias(Network);
export const LuSlidersHorizontal = alias(SlidersHorizontal);
export const LuZap = alias(Zap);
// react-icons/md
export const MdAddCircle = alias(CirclePlus);
export const MdAddToDrive = alias(HardDrive);
export const MdArrowBack = alias(ArrowLeft);
export const MdArrowForward = alias(ArrowRight);
export const MdBusiness = alias(Building2);
export const MdCalendarToday = alias(CalendarDays);
export const MdCancel = alias(CircleX);
export const MdChecklist = alias(ListChecks);
export const MdDragIndicator = alias(GripVertical);
export const MdFormatListBulletedAdd = alias(ListPlus);
export const MdGavel = alias(Gavel);
export const MdInfoOutline = alias(Info);
export const MdInsertDriveFile = alias(File);
export const MdKeyboard = alias(Keyboard);
export const MdLink = alias(Link);
export const MdMonitor = alias(Monitor);
export const MdOutlineBadge = alias(IdCard);
export const MdOutlineHeadsetMic = alias(Headset);
export const MdOutlineHowToReg = alias(UserCheck);
export const MdOutlineInsertInvitation = alias(CalendarDays);
export const MdOutlinePeople = alias(Users);
export const MdOutlineSettings = alias(Settings);
export const MdOutlineUnfoldMore = alias(ChevronsUpDown);
export const MdSupportAgent = alias(Headset);
export const MdUnfoldLess = alias(ChevronsDownUp);
export const MdUnfoldMore = alias(ChevronsUpDown);
// react-icons/pi
export const PiEye = alias(Eye);
export const PiMicrosoftWordLogo = WordIcon;
export const PiNewspaperClippingLight = alias(Newspaper);
export const PiPiggyBank = alias(PiggyBank);
export const PiSignature = alias(Signature);
// react-icons/ri
export const RiComputerLine = alias(Monitor);
// react-icons/si
export const SiGoogle = GoogleIcon;
export const SiMicrosoft = MicrosoftIcon;
export const SiOkta = OktaIcon;

export { BookCheck as MdFactCheck };
